// Phase 29 (item 1.ii, Stage-3 C9) — "due today or overdue" selector for the
// compact column. No such horizon existed before (Dashboard only has overdue
// + 7-day-window views). Pure and dependency-free so it can be exercised
// headlessly with node against fixtures.
//
// Open rows (counted by `count`, i.e. the badge):
//   • tasks: open status (OPEN_ST), not archived, own dueDate <= today;
//   • subtasks: not done, own dueDate <= today, parent task open + not
//     archived (the parent's own dueDate is irrelevant for these rows).
// Done-today rows (Stage-3 C9 — listed, flagged done, NOT counted):
//   • tasks completed TODAY (status completed + completedAt today — 209 §24:
//     the model records the completion now; `updatedAt`, which any later edit
//     moves, is read only for a row the deploy's fill has not reached) whose
//     dueDate still qualifies (<= today);
//   • subtasks checked done TODAY (done + completedAt today, dueDate <= today)
//     whose parent is open or itself completed today.
// Event subtasks (parentEventId) are out of scope — the events section
// counts events only.
//
// Stage-3 C60 — delegation-aware viewer scoping. With the optional 4th
// `viewerId` argument the selector applies the "my tasks" ownership rules
// (so callers stop pre-filtering on assignedCoId):
//   • task rows: only tasks ASSIGNED to the viewer, at the PARENT due date
//     (the delegator keeps the parent date);
//   • subtask rows: only subtasks that are MINE (subtaskIsMine — assigned to
//     me, or unassigned under a parent assigned to me), at the SUBTASK's own
//     due date. A task that is mine only via a delegated subtask therefore
//     surfaces exactly when MY subtask is due/overdue — the viewer-relevant
//     date — and leaves the list when my last open assigned subtask is done
//     (it then only lingers same-day in doneSubRows, the uncounted C9
//     feedback bucket).
// Without viewerId the legacy unscoped behaviour is byte-identical.
//
// Stage-3 C65 — delegation now MATERIALIZES server-side as a temporary
// parent assignment (Task.delegatedAssigned, wire `delegatedCoIds`,
// maintained by server/src/lib/delegation.js). DELIBERATE RECONCILIATION:
// these client rules stay purely computed from subtasks — by construction
// the server set equals rule (b) ("≥1 OPEN subtask assigned to me"), so
// consulting delegatedCoIds here would be redundant where consistent and
// WRONG where momentarily stale (ticking my last subtask must drop the task
// from my lists instantly, before the retraction event lands). The
// materialized column exists for what the client cannot do: SSE delivery to
// off-team delegatees, case-title/doc-link exposure on their rows, and the
// chronology record. Tasks are neither double-counted (rows ride either the
// task bucket or MY subtask rows, as before) nor lost (task payloads carry
// their subtasks; upsert preserves them on include-less payloads).
//
// Date fields can be date-only strings or full ISO timestamps — compare on
// the YYYY-MM-DD part only (matches the a.date.slice(0,10) convention used
// across the app).

export const datePart = (d: any) => (d ? String(d).slice(0, 10) : "");

// C60 — a subtask is mine when assigned to me; an UNASSIGNED subtask belongs
// to whoever the parent is assigned to.
export const subtaskIsMine = (st: any, task: any, uid: number | null | undefined) =>
  uid != null &&
  !!st &&
  (st.assignedCoId != null
    ? st.assignedCoId === uid
    : !!task && task.assignedCoId === uid);

// C60 — a task is mine when (a) the parent is assigned to me, or (b) it has
// at least one OPEN (not done) subtask assigned to me.
// Round 13 (C75) — an ARCHIVED subtask is retired: it can't make a task mine.
export const taskIsMine = (t: any, uid: number | null | undefined) =>
  uid != null &&
  !!t &&
  (t.assignedCoId === uid ||
    (Array.isArray(t.subtasks) &&
      t.subtasks.some(
        (st: any) => st && !st.done && !st.archived && st.assignedCoId === uid,
      )));

// C60 — the date a viewer sees/sorts a task by: the parent due date for its
// assignee (raw, byte-compatible with the pre-C60 comparisons), the EARLIEST
// open assigned subtask date (date-only) for a mine-via-subtask viewer.
// Falls back to the parent due date when no dated open assigned subtask
// exists (display-only fallback; due-today membership never uses it).
export const viewerDueDate = (t: any, uid: number | null | undefined) => {
  if (!t) return null;
  if (uid == null || t.assignedCoId === uid) return t.dueDate || null;
  let best: string | null = null;
  for (const st of Array.isArray(t.subtasks) ? t.subtasks : []) {
    if (!st || st.done || st.archived || st.assignedCoId !== uid || !st.dueDate)
      continue;
    const d = datePart(st.dueDate);
    if (!best || d < best) best = d;
  }
  return best != null ? best : t.dueDate || null;
};

/** 209 §24 — when a row was completed: `completedAt`, else — for a row the deploy's fill has not reached — `updatedAt`. */
type Completion = { completedAt?: string | null; updatedAt?: string | null };
const completedDay = (r: Completion) => r.completedAt ?? r.updatedAt;

export function selectDueToday(tk: any[], OPEN_ST: string[], todayISO: string, viewerId?: number | null) {
  const tasks = [];
  const subRows = [];
  const doneTasks = [];
  const doneSubRows = [];
  const scoped = viewerId != null;
  for (const t of Array.isArray(tk) ? tk : []) {
    if (!t || t.archived) continue;
    const open = OPEN_ST.includes(t.status);
    const doneToday =
      t.status === "completed" && datePart(completedDay(t as Completion)) === todayISO;
    const dueQualifies = t.dueDate && datePart(t.dueDate) <= todayISO;
    // C60 — scoped task rows ride rule (a) only (parent assigned to viewer);
    // mine-via-subtask tasks surface through their subRows below.
    const taskRowOk = !scoped || t.assignedCoId === viewerId;
    if (taskRowOk && dueQualifies) {
      if (open) tasks.push(t);
      else if (doneToday) doneTasks.push(t);
    }
    if (!open && !doneToday) continue;
    for (const st of Array.isArray(t.subtasks) ? t.subtasks : []) {
      // Round 13 (C75) — archived subtask rows are out of due-today entirely.
      if (!st || st.archived || !st.dueDate || datePart(st.dueDate) > todayISO)
        continue;
      if (scoped && !subtaskIsMine(st, t, viewerId)) continue;
      if (!st.done) {
        if (open) subRows.push({ st, task: t });
      } else if (datePart(completedDay(st as Completion)) === todayISO) {
        doneSubRows.push({ st, task: t });
      }
    }
  }
  return {
    tasks,
    subRows,
    doneTasks,
    doneSubRows,
    count: tasks.length + subRows.length,
  };
}

/* ── 209 §27 [Laurent] — THE HOURGLASS VIEW'S TWO LISTS ──────────────────────
   « Tâches et événements du jour » and « Tâches en retard », from the
   selector's own buckets, so the scope stays C60's (mine, by the date that is
   mine — the task's for its assignee, my subtask's for a delegatee). One row
   per TASK, as the column has always shown them (the row carries its subtask
   counter and checklist):
     • late — a task due before today, or holding a subtask of mine due before
       today: anything late makes the row late, so it shows once, there;
     • today — due today (the task, or my subtask), open first, then the rows
       done today (the uncounted C9 feedback, as before).
   A timed task leaves both while its timer exists — that is the column's
   business (it knows the timers), not this selector's. */
interface SplitRow { id?: unknown; dueDate?: string | null }
interface DueBuckets<T extends SplitRow> {
  tasks: readonly T[];
  subRows: readonly { st: SplitRow; task: T }[];
  doneTasks: readonly T[];
  doneSubRows: readonly { st: SplitRow; task: T }[];
}
export function splitDueToday<T extends SplitRow>(due: DueBuckets<T>, todayISO: string): { today: T[]; late: T[] } {
  const late: T[] = [];
  const today: T[] = [];
  const seen = new Set<unknown>();
  const push = (arr: T[], t: T) => {
    if (!seen.has(t.id)) {
      seen.add(t.id);
      arr.push(t);
    }
  };
  for (const t of due.tasks) if (datePart(t.dueDate) < todayISO) push(late, t);
  for (const { st, task } of due.subRows) if (datePart(st.dueDate) < todayISO) push(late, task);
  for (const t of due.tasks) push(today, t);
  for (const { task } of due.subRows) push(today, task);
  for (const t of due.doneTasks) push(today, t);
  for (const { task } of due.doneSubRows) push(today, task);
  return { today, late };
}
